import { useState } from "react";
import { format, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  SlidersHorizontal,
  Shapes,
  Users,
  Bell,
  Clock,
  CalendarDays,
  type LucideIcon,
} from "lucide-react";
import {
  FilterOptionRow,
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelEmpty,
  FilterPanelHeader,
  FilterSection,
  FilterSectionNote,
  FilterSections,
  FilterSwitchRow,
} from "@/components/filters/FilterPanel";
import { FilterChips } from "@/components/filters/FilterChips";
import { matchesFilterQuery } from "@/components/filters/matchesFilterQuery";
import { multiValueChip, singleValueChip, type FilterChip } from "@/components/filters/filterChip";
import { CONTROL_ACTIVE_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import { AssigneeAvatar } from "@/domains/pm/components/shared/AssigneeAvatar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ACTIVITY_CATEGORIES } from "@/domains/pm/utils/activityMeta";
import type { UserProfile } from "@/domains/pm/types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";
import {
  appliedCustomRange,
  hasTimeRangeFilter,
  type NotificationsCustomRange,
  type NotificationsTimeRange,
} from "@/domains/pm/utils/notificationsTimeRange";

export type { NotificationsTimeRange, NotificationsCustomRange };
/** Active filter selection for the Notifications feed. Empty arrays + false +
 *  "all" means "no filter". Kept flat so wiring into the repo opts is direct. */
export interface NotificationsFilterValue {
  categories: string[]; // ACTIVITY_CATEGORIES keys
  actorIds: number[];
  unreadOnly: boolean;
  timeRange: NotificationsTimeRange;
  customRange: NotificationsCustomRange | null;
}

export const EMPTY_NOTIFICATIONS_FILTER: NotificationsFilterValue = {
  categories: [],
  actorIds: [],
  unreadOnly: false,
  timeRange: "all",
  customRange: null,
};

export function isNotificationsFilterActive(v: NotificationsFilterValue): boolean {
  return v.categories.length > 0 || v.actorIds.length > 0 || v.unreadOnly || hasTimeRangeFilter(v);
}

type Dimension = "type" | "person" | "unread" | "timeRange";
/** Dimensions with options; "unread" is a switch row under them. */
const SECTIONS: Dimension[] = ["type", "person", "timeRange"];
const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  type: Shapes,
  person: Users,
  unread: Bell,
  timeRange: Clock,
};

const TIME_RANGES: Exclude<NotificationsTimeRange, "custom">[] = ["all", "today", "7d", "30d"];

function formatDay(day: string, language: "en" | "et"): string {
  return parseISO(day).toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function userName(u: UserProfile): string {
  const n = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return n || u.email;
}

interface Props {
  users: UserProfile[];
  value: NotificationsFilterValue;
  onChange: (next: NotificationsFilterValue) => void;
}

/**
 * Filter control for the Notifications feed, on the shared filter panel like
 * every other page: activity type, person and time range as sections that
 * open in place, unread as a switch, and a custom range picked on a calendar
 * inside the Time range section. Active filters show as removable chips to
 * the left of the button.
 */
export function NotificationsFilterControl({ users, value, onChange }: Props) {
  const { t, language } = useLanguage();
  const f = t.pm.notifications.filter;
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  // The calendar opens inside the Time range section, under its presets.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const [query, setQuery] = useState("");

  const dimensionLabel = (d: Dimension): string =>
    d === "type" ? f.type : d === "person" ? f.person : d === "unread" ? f.unread : f.timeRange;
  const categoryLabel = (key: string): string =>
    f.categories[key as keyof typeof f.categories] ?? key;
  const rangeLabel = (r: Exclude<NotificationsTimeRange, "custom">): string =>
    f.ranges[r === "7d" ? "last7" : r === "30d" ? "last30" : r];
  const customLabel = (range: NotificationsCustomRange): string =>
    range.from === range.to
      ? formatDay(range.from, language)
      : `${formatDay(range.from, language)} – ${formatDay(range.to, language)}`;

  // The applied custom range, only when the feed is actually filtered by it —
  // the check mark, label and calendar seed must agree with the chip and the
  // query (and an unparseable range never reaches `format`).
  const appliedCustom = appliedCustomRange(value);

  // Seed the calendar with the applied range so re-opening it edits, not restarts.
  const openCustomRange = () => {
    setDraft(
      appliedCustom
        ? { from: parseISO(appliedCustom.from), to: parseISO(appliedCustom.to) }
        : undefined
    );
    setQuery("");
    setSection("timeRange");
    setCalendarOpen(true);
  };

  const openAt = (d: Dimension) => {
    if (d === "timeRange" && value.timeRange === "custom") openCustomRange();
    else setSection(d === "unread" ? null : d);
    setOpen(true);
  };

  const closePanel = () => {
    setOpen(false);
    setSection(null);
    setCalendarOpen(false);
    setQuery("");
  };

  const applyCustomRange = () => {
    // Both ends, as the hint asks; one day is that day picked twice.
    if (!draft?.from || !draft.to) return;
    const [start, last] = draft.from <= draft.to ? [draft.from, draft.to] : [draft.to, draft.from];
    onChange({
      ...value,
      timeRange: "custom",
      customRange: { from: format(start, "yyyy-MM-dd"), to: format(last, "yyyy-MM-dd") },
    });
    closePanel();
  };

  const toggleCategory = (key: string) => {
    const has = value.categories.includes(key);
    onChange({
      ...value,
      categories: has ? value.categories.filter((k) => k !== key) : [...value.categories, key],
    });
  };

  const chips: FilterChip<Dimension>[] = [];
  if (value.categories.length > 0) {
    chips.push(
      multiValueChip("type", f.type, value.categories.map(categoryLabel), () =>
        onChange({ ...value, categories: [] })
      )
    );
  }
  if (value.actorIds.length > 0) {
    // Ids ride along so the "+N more" list can draw each person's picture.
    const names = value.actorIds.flatMap((id) => {
      const u = users.find((candidate) => candidate.id === id);
      return u ? [{ id: String(id), label: userName(u) }] : [];
    });
    chips.push(
      multiValueChip("person", f.person, names, () => onChange({ ...value, actorIds: [] }), {
        selectedCount: value.actorIds.length,
      })
    );
  }
  if (value.unreadOnly) {
    chips.push(
      singleValueChip("unread", f.unreadOnly, () => onChange({ ...value, unreadOnly: false }))
    );
  }
  if (hasTimeRangeFilter(value)) {
    chips.push(
      singleValueChip(
        "timeRange",
        appliedCustom
          ? customLabel(appliedCustom)
          : rangeLabel(value.timeRange as Exclude<NotificationsTimeRange, "custom">),
        () => onChange({ ...value, timeRange: "all", customRange: null }),
        f.timeRange
      )
    );
  }

  const searching = query.trim().length > 0;
  const categoryRow = (key: string, inSearch = false) => (
    <FilterOptionRow
      key={`type-${key}`}
      label={categoryLabel(key)}
      ariaLabel={`${f.type}: ${categoryLabel(key)}`}
      checked={value.categories.includes(key)}
      onToggle={() => toggleCategory(key)}
      sublabel={inSearch ? f.type : undefined}
    />
  );
  const personRow = (u: UserProfile, inSearch = false) => (
    <FilterOptionRow
      key={`person-${u.id}`}
      label={userName(u)}
      ariaLabel={`${f.person}: ${userName(u)}`}
      leading={<AssigneeAvatar user={u} size={18} />}
      checked={value.actorIds.includes(u.id)}
      onToggle={() =>
        onChange({
          ...value,
          actorIds: value.actorIds.includes(u.id)
            ? value.actorIds.filter((id) => id !== u.id)
            : [...value.actorIds, u.id],
        })
      }
      sublabel={inSearch ? f.person : undefined}
    />
  );
  // One choice at a time: picking a preset replaces the range.
  const rangeRow = (r: Exclude<NotificationsTimeRange, "custom">, inSearch = false) => (
    <FilterOptionRow
      key={`range-${r}`}
      label={rangeLabel(r)}
      ariaLabel={`${f.timeRange}: ${rangeLabel(r)}`}
      checked={r === "all" ? !hasTimeRangeFilter(value) : value.timeRange === r}
      single
      onToggle={() => {
        setCalendarOpen(false);
        onChange({ ...value, timeRange: r, customRange: null });
      }}
      sublabel={inSearch ? f.timeRange : undefined}
    />
  );
  const unreadRow = (
    <FilterSwitchRow
      key="unread"
      icon={DIMENSION_ICON.unread}
      label={f.unreadOnly}
      checked={value.unreadOnly}
      onCheckedChange={(on) => onChange({ ...value, unreadOnly: on })}
    />
  );

  const calendar = (
    <div>
      <div className="flex justify-center">
        <Calendar
          mode="range"
          selected={draft}
          // Once a range is complete, the next click starts a new one
          // at that day. react-day-picker would otherwise stretch the
          // old range to it, or clear it when it is one of its ends.
          onSelect={(range, day) =>
            setDraft(draft?.from && draft.to ? { from: day, to: undefined } : range)
          }
          defaultMonth={draft?.from}
          disabled={{ after: new Date() }}
          numberOfMonths={1}
        />
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-xs text-muted-foreground">
          {draft?.from && draft.to
            ? customLabel({
                from: format(draft.from, "yyyy-MM-dd"),
                to: format(draft.to, "yyyy-MM-dd"),
              })
            : f.customRangeHint}
        </span>
        <Button size="sm" onClick={applyCustomRange} disabled={!draft?.from || !draft.to}>
          {f.apply}
        </Button>
      </div>
    </div>
  );

  const sectionBody = (d: Dimension) => {
    if (d === "type") return ACTIVITY_CATEGORIES.map((c) => categoryRow(c.key));
    if (d === "person") {
      return users.length > 0 ? (
        users.map((u) => personRow(u))
      ) : (
        <FilterSectionNote>{t.pm.issues.noUsersFound}</FilterSectionNote>
      );
    }
    return (
      <>
        {TIME_RANGES.map((r) => rangeRow(r))}
        <FilterOptionRow
          label={appliedCustom ? customLabel(appliedCustom) : f.ranges.custom}
          ariaLabel={`${f.timeRange}: ${f.ranges.custom}`}
          leading={<CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />}
          checked={appliedCustom !== null}
          single
          // Ticked: unticking clears the range. Edit an applied range from its chip.
          onToggle={() => {
            if (appliedCustom) {
              setCalendarOpen(false);
              onChange({ ...value, timeRange: "all", customRange: null });
            } else if (calendarOpen) setCalendarOpen(false);
            else openCustomRange();
          }}
        />
        {calendarOpen && calendar}
      </>
    );
  };

  const sectionCount = (d: Dimension): number =>
    d === "type"
      ? value.categories.length
      : d === "person"
        ? value.actorIds.length
        : Number(hasTimeRangeFilter(value));
  const clearSection = (d: Dimension) =>
    onChange(
      d === "type"
        ? { ...value, categories: [] }
        : d === "person"
          ? { ...value, actorIds: [] }
          : { ...value, timeRange: "all", customRange: null }
    );

  const searchResults = () => {
    const rows = [
      ...ACTIVITY_CATEGORIES.filter((c) => matchesFilterQuery(categoryLabel(c.key), query)).map(
        (c) => categoryRow(c.key, true)
      ),
      ...users.filter((u) => matchesFilterQuery(userName(u), query)).map((u) => personRow(u, true)),
      ...TIME_RANGES.filter((r) => matchesFilterQuery(rangeLabel(r), query)).map((r) =>
        rangeRow(r, true)
      ),
      ...(matchesFilterQuery(f.unreadOnly, query) ? [unreadRow] : []),
    ];
    if (rows.length === 0) {
      return (
        <FilterPanelEmpty>
          {t.common.filterPanel.noMatches.replace("{query}", () => query.trim())}
        </FilterPanelEmpty>
      );
    }
    return <FilterPanelBody className="py-1">{rows}</FilterPanelBody>;
  };

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      <FilterChips
        chips={chips}
        onOpenDimension={openAt}
        onClearAll={() => onChange(EMPTY_NOTIFICATIONS_FILTER)}
        valueIcon={(d, id) => {
          const u = d === "person" ? users.find((c) => String(c.id) === id) : undefined;
          return u ? <AssigneeAvatar user={u} size={16} /> : null;
        }}
      />
      <Popover open={open} onOpenChange={(o) => (o ? setOpen(true) : closePanel())} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="control"
            aria-label={chips.length > 0 ? `${f.title} (${chips.length})` : f.title}
            className={cn("gap-1.5", chips.length > 0 && CONTROL_ACTIVE_CLASS)}
            {...pageInfoTarget("notifications.filter")}
          >
            <SlidersHorizontal className="opacity-60" />
            {f.title}
            {chips.length > 0 && (
              <span
                aria-hidden="true"
                className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground"
              >
                {chips.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <FilterPanelContent align="end">
          <FilterPanelHeader
            title={t.common.filterPanel.active}
            activeCount={chips.length}
            clearLabel={t.common.filterPanel.clearAll}
            onClearAll={() => onChange(EMPTY_NOTIFICATIONS_FILTER)}
            search={{
              value: query,
              onChange: setQuery,
              placeholder: t.common.filterPanel.search,
              clearLabel: t.common.filterPanel.clearSearch,
            }}
          />
          {searching ? (
            searchResults()
          ) : (
            <FilterPanelBody>
              <FilterSections value={section} onValueChange={setSection}>
                {SECTIONS.map((d) => (
                  <FilterSection
                    key={d}
                    value={d}
                    icon={DIMENSION_ICON[d]}
                    label={dimensionLabel(d)}
                    selectedCount={sectionCount(d)}
                    onClear={() => clearSection(d)}
                    clearLabel={t.common.filterPanel.clearAll}
                    // The calendar is taller than the usual option cap.
                    contentClassName={d === "timeRange" ? "max-h-none" : undefined}
                    singleChoice={d === "timeRange"}
                  >
                    {sectionBody(d)}
                  </FilterSection>
                ))}
              </FilterSections>
              {unreadRow}
            </FilterPanelBody>
          )}
        </FilterPanelContent>
      </Popover>
    </div>
  );
}
